import React, { FunctionComponent, useEffect, useState } from 'react';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { ruRU } from '@mui/material/locale';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import { useParams } from 'react-router-dom';
import {
  Box,
  createTheme,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  ThemeProvider,
  Typography,
} from '@mui/material';
import { BackDropDiv } from '../backdrop/BackDropDiv';
import { IApiResult } from '../../interface/IApiResult';
import { IAlertProps } from '../../interface/IAlertProps';
import { getDefaultAlertProps, getPageNumId } from '../../utils/utils';
import { API_STATUS_PROBLEM } from '../../constant/constants';
import { AlertSeverityEnum } from '../../enum/AlertSeverityEnum';
import { MyButton } from '../UI/MyButton';
import { MyAlert } from '../UI/MyAlert';
import { CloudDto } from '../../dto/CloudDto';
import { apiGetClouds } from '../../service/apiGetClouds';
import { apiDeleteCloud } from '../../service/apiDeleteCloud';
import { apiUpdateCloud } from '../../service/apiUpdateCloud';
import { DialogConfirmation } from '../confirmation/DialogConfirmation';
import { apiAddCloudLink } from '../../service/apiAddCloudLink';

interface Props {
  idPage: string;
  open: boolean;
  titleCloud: string;
  isEdit: boolean;
  onClose: () => void;
  onSave: (e: any) => void;
}

export const DialogGetFromCloud: FunctionComponent<Props> = ({
  idPage,
  open,
  titleCloud,
  isEdit,
  onClose,
  onSave,
}) => {
  const [cloudDtos, setCloudDtos] = useState<CloudDto[]>([]);
  const [alertProps, setAlertProps] = useState<IAlertProps>(
    getDefaultAlertProps()
  );
  const [cloudDto, setCloudDto] = useState<CloudDto>({
    id: '',
    name: '',
  });
  const [openDialogConfirmation, setOpenDialogConfirmation] = useState(false);
  const [selctedId, setSelectedId] = useState('');
  const [backDrop, setBackDrop] = useState(false);
  const handleClose = () => {
    setCloudDtos([]);
    onClose();
  };
  const handleSave = (id: string) => {
    onSave(id);
  };

  const [title, setTitle] = useState('');

  const getCloud = async () => {
    setBackDrop(true);
    const apiResult: IApiResult = await apiGetClouds();
    if (apiResult.isSuccess) {
      setCloudDtos(apiResult.data);
    } else {
      setAlertProps({
        message: API_STATUS_PROBLEM,
        severity: AlertSeverityEnum.error,
      });
    }
    setBackDrop(false);
  };

  const handleSaveCloud = async (dto: CloudDto) => {
    setBackDrop(true);
    const apiResult: IApiResult = await apiUpdateCloud(dto);
    if (apiResult.isSuccess) {
      getCloud();
    } else {
      setAlertProps({
        message: API_STATUS_PROBLEM,
        severity: AlertSeverityEnum.error,
      });
    }
    setBackDrop(false);
  };

  const handleDeleteCloud = (dto: CloudDto) => {
    setCloudDto(dto);
    setOpenDialogConfirmation(true);
  };

  const deleteCloud = async (confirm: boolean) => {
    if (!confirm) {
      setOpenDialogConfirmation(false);
      return;
    }
    setBackDrop(true);
    const apiResult: IApiResult = await apiDeleteCloud(cloudDto.id);
    if (apiResult.isSuccess) {
      getCloud();
    } else {
      setAlertProps({
        message: API_STATUS_PROBLEM,
        severity: AlertSeverityEnum.error,
      });
    }
    setBackDrop(false);
    setOpenDialogConfirmation(false);
  };

  const [, setCheckHelper] = useState(false);
  const { section, route } = useParams();
  const page = getPageNumId(idPage);
  const addcloudLink = async () => {
    setBackDrop(true);
    const apiResult: IApiResult = await apiAddCloudLink(
      section ?? '',
      route ?? '',
      page ?? '',
      selctedId,
      title
    );
    if (apiResult.isSuccess) {
      handleSave(selctedId);
      handleClose();
    } else {
      setAlertProps({
        message: API_STATUS_PROBLEM,
        severity: AlertSeverityEnum.error,
      });
    }
    setBackDrop(false);
  };

  const theme = createTheme(
    {
      palette: {
        primary: { main: '#1976d2' },
      },
    },
    ruRU
  );

  const changeSelect = (e: any) => {
    let idNewFormatSelect = '';
    for (let i = 0; i < e.length; i += 1) {
      idNewFormatSelect = `${idNewFormatSelect + e[i]}**`;
    }
    idNewFormatSelect = idNewFormatSelect.substring(
      0,
      idNewFormatSelect.length - 2
    );
    setSelectedId(idNewFormatSelect);
  };

  const other = {
    autoHeight: true,
    showCellRightBorder: true,
    showColumnRightBorder: true,
  };

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'ID',
      renderHeader: () => <strong>ID</strong>,
      editable: false,
      sortable: false,
      filterable: false,
      renderCell: (row) => row.row.id,
    },
    {
      field: 'name',
      headerName: 'Идентификатор',
      renderHeader: () => <strong>Идентификатор</strong>,
      flex: 1,
      minWidth: 200,
      editable: isEdit,
      renderCell: (row) => row.row.name,
    },
  ];

  const columnsEdit: GridColDef[] = [
    {
      field: 'id',
      headerName: 'ID',
      renderHeader: () => <strong>ID</strong>,
      editable: false,
      sortable: false,
      filterable: false,
      renderCell: (row) => row.row.id,
    },
    {
      field: 'name',
      headerName: 'Идентификатор',
      renderHeader: () => <strong>Идентификатор</strong>,
      flex: 1,
      minWidth: 200,
      editable: isEdit,
      renderCell: (row) => row.row.name,
    },
    {
      field: 'id2',
      type: 'actions',
      width: 70,
      headerName: '',
      editable: false,
      sortable: false,
      filterable: false,
      renderCell: (row) => (
        <Stack direction="row" width="100%" justifyContent="center">
          <IconButton size="small" onClick={() => handleSaveCloud(row.row)}>
            <SaveIcon
              fontSize="small"
              sx={{
                color: '#03a9f4',
              }}
            />
          </IconButton>
          <IconButton size="small" onClick={() => handleDeleteCloud(row.row)}>
            <DeleteIcon
              fontSize="small"
              sx={{
                color: 'red',
              }}
            />
          </IconButton>
        </Stack>
      ),
    },
  ];
  const handleAdd = async () => {
    await addcloudLink();
  };

  useEffect(() => {
    if (open) {
      getCloud();
      setCheckHelper(false);
      setTitle(titleCloud);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <div>
      <BackDropDiv open={backDrop} />
      <Dialog
        open={open}
        fullWidth
        maxWidth="md"
        style={{ userSelect: 'none' }}
        PaperProps={{ sx: { backgroundColor: '#e7ebee', width: '100%' } }}
      >
        <MyAlert
          message={alertProps.message}
          severity={alertProps.severity}
          onClose={() => setAlertProps({ ...alertProps, message: '' })}
        />
        <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography
              style={{
                fontFamily: 'sans-serif',
                fontSize: '16px',
                textAlign: 'left',
                fontWeight: '600',
              }}
              component="text"
            >
              Ссылки
            </Typography>
            <IconButton size="medium" onClick={onClose}>
              <CloseIcon fontSize="medium" />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack padding="0px" spacing={1}>
            <Box
              sx={{
                '& .app-theme-cell': {
                  fontFamily: 'sans-serif',
                },
              }}
            >
              <ThemeProvider theme={theme}>
                <DataGrid
                  sx={{
                    '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell': {
                      py: 1,
                      fontSize: '1px',
                    },
                    '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': {
                      py: '5px',
                      alignContent: 'center',
                    },
                    '&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell':
                      {
                        py: '5px',
                      },
                    '&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus, &.MuiDataGrid-root .MuiDataGrid-cell:focus-within':
                      {
                        outline: 'none !important',
                      },
                  }}
                  getRowHeight={() => 'auto'}
                  columns={!isEdit ? columns : columnsEdit}
                  rows={cloudDtos}
                  initialState={{
                    pagination: {
                      paginationModel: {
                        pageSize: 10,
                      },
                    },
                  }}
                  onRowSelectionModelChange={(e) => changeSelect(e)}
                  pageSizeOptions={[10]}
                  checkboxSelection={!isEdit}
                  disableRowSelectionOnClick
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...other}
                />
              </ThemeProvider>
            </Box>
            <Stack paddingTop="20px" alignItems="center">
              {!isEdit && (
                <MyButton
                  width="120px"
                  text="Добавить"
                  background="#9c27b0"
                  height="50px"
                  onClick={handleAdd}
                />
              )}
            </Stack>
          </Stack>
        </DialogContent>
      </Dialog>
      <DialogConfirmation
        open={openDialogConfirmation}
        message="Вы точно хотите удалить данную ссылку?"
        onClose={deleteCloud}
      />
    </div>
  );
};
